import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ReceiptText,
  Target,
  CalendarClock,
  Gauge,
  FileWarning,
  ScanSearch,
  FileBarChart,
  Archive,
  Users,
  Bell,
  Plug,
  UploadCloud,
  Wallet,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06 } }),
};

const categories = [
  {
    label: "Accountability",
    tag: "Own your numbers",
    features: [
      {
        icon: Target,
        title: "Cost Per Retained Case",
        body: "Not cost per lead — the metric that maps marketing dollars to signed cases by channel, campaign, vendor, practice area and office.",
      },
      {
        icon: Gauge,
        title: "Vendor Scorecards",
        body: "Compare your Google Ads agency vs. TV buyer vs. billboard company vs. directory on spend, signed cases, ROI and responsiveness.",
      },
      {
        icon: FileWarning,
        title: "Intake vs. Marketing Failure",
        body: "Separate poor leads from good leads your team dropped. See which qualified callers were never contacted within 15 minutes.",
      },
      {
        icon: CalendarClock,
        title: "Contract & Renewal Tracking",
        body: "Never get locked into an underperforming vendor again. Track terms and get alerts 30/60/90 days before auto-renewal deadlines.",
      },
    ],
  },
  {
    label: "AI & Automation",
    tag: "Let the machine audit",
    features: [
      {
        icon: ScanSearch,
        title: "AI Invoice OCR",
        body: "Forward vendor invoices as PDFs or images. AI extracts totals, line items and dates automatically — no manual data entry.",
      },
      {
        icon: ReceiptText,
        title: "Invoice Reconciliation",
        body: "AI compares invoices against contracts and platform spend, flagging duplicate charges, unapproved increases and fees that exceed the agreement.",
      },
      {
        icon: Bell,
        title: "Weekly Digest Alerts",
        body: "An automated weekly email digest surfaces overbilling flags, renewal deadlines and spend anomalies — delivered to your inbox.",
      },
    ],
  },
  {
    label: "Reporting",
    tag: "Board-ready in one click",
    features: [
      {
        icon: FileBarChart,
        title: "Executive PDF Reports",
        body: "Generate an AI-written, board-ready executive report covering spend, ROI, vendor performance and cost per retained case — as a polished PDF.",
      },
      {
        icon: Wallet,
        title: "Budget vs. Actual",
        body: "Track planned vs. actual marketing spend across offices and vendors, with month-over-month trends and variance highlights.",
      },
      {
        icon: Archive,
        title: "Advertising Compliance Archive",
        body: "Store and timestamp every ad creative, disclaimer and campaign asset in a searchable archive for bar compliance and audits.",
      },
    ],
  },
  {
    label: "Team & Access",
    tag: "The right eyes, the right data",
    features: [
      {
        icon: Users,
        title: "Role-Based Access Control",
        body: "Invite partners, marketing staff and finance with granular roles. Everyone sees exactly what they should — nothing more.",
      },
      {
        icon: ShieldCheck,
        title: "Multi-Office & Firm Isolation",
        body: "Manage multiple offices under one firm with strict data isolation between firms and client accounts.",
      },
    ],
  },
  {
    label: "Data & Integrations",
    tag: "Connect everything",
    features: [
      {
        icon: Plug,
        title: "Ad Platform & Call-Tracking Integrations",
        body: "Connect CallRail, Meta Ads, Google Ads and Clio using your own credentials to pull spend, call and case data directly — independent of vendor dashboards.",
      },
      {
        icon: UploadCloud,
        title: "Bulk CSV Imports",
        body: "Onboard historical vendors, budgets, leads and invoices in minutes with bulk CSV uploads — no re-keying required.",
      },
    ],
  },
];

export default function Features() {
  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden" data-testid="features-page">
      <Seo title="Features — RetainProof marketing accountability for law firms" description="Reconcile marketing spend against vendor invoices, connect CallRail, Meta Ads, Google Ads and Clio, and see cost per retained case in one independent dashboard." path="/features" />
      <div className="grain-overlay" />

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-body font-bold text-2xl tracking-tight" data-testid="features-brand-logo">
            Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/consultation" data-testid="features-nav-audit">
              <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                Request audit
              </Button>
            </Link>
            <Link to="/consultation" data-testid="features-nav-register">
              <Button className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium">
                Request a consultation
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-40 pb-20">
        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-6"
        >
          Everything RetainProof™ does
        </motion.p>
        <motion.h1
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={1}
          className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl"
        >
          One independent system for <span className="italic text-[#D5A44A]">every marketing dollar.</span>
        </motion.h1>
        <motion.p
          initial="hidden"
          animate="show"
          variants={fadeUp}
          custom={2}
          className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed"
        >
          From AI invoice reconciliation to cost per retained case, compliance archives and
          board-ready reports — here is the full toolkit that holds your vendors accountable.
        </motion.p>
      </section>

      {/* Categories */}
      {categories.map((cat, ci) => (
        <section
          key={cat.label}
          className={`relative z-10 border-t border-white/10 ${ci % 2 === 0 ? "bg-[#0B1F33]" : ""}`}
          data-testid={`features-category-${cat.label.toLowerCase().replace(/\W/g, "-")}`}
        >
          <div className="max-w-7xl mx-auto px-6 py-20">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
              <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">{cat.label}</h2>
              <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#0E7C7B]">{cat.tag}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cat.features.map((f, i) => (
                <motion.div
                  key={f.title}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="border border-white/10 bg-[#12324D] p-8 flex flex-col transition-colors hover:border-[#D5A44A]/40"
                  data-testid={`feature-card-${f.title.toLowerCase().replace(/\W+/g, "-")}`}
                >
                  <f.icon className="h-7 w-7 text-[#D5A44A]" />
                  <h3 className="font-display text-2xl mt-6">{f.title}</h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight">
            See it on your own numbers.
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Request a consultation, or start with an Executive Marketing Audit.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/consultation" data-testid="features-cta-register">
              <Button className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group">
                Request a consultation
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/consultation" data-testid="features-cta-audit">
              <Button
                variant="outline"
                className="rounded-none h-12 px-8 text-base border-white/20 bg-transparent text-white hover:bg-white/5"
              >
                Request an audit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm text-zinc-500">
          <Link to="/" className="font-body font-bold text-xl text-white">
            Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup>
          </Link>
          <span className="font-mono-data text-xs">Independent marketing accountability for law firms · 2026</span>
        </div>
      </footer>
    </div>
  );
}
